if (!customElements.get('product-card-image-slider')) {
  class ProductCardImageSlider extends HTMLElement {
    static activeAutoplay = null;

    static observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.prepare();
            ProductCardImageSlider.observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '500px 0px' })
      : null;

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productCard = this.closest('product-card');
      this.item = this.querySelector('.slider__item');
      this.link = this.item?.querySelector('.js-prod-link');
      this.image = this.item?.querySelector('img');
      this.prevBtn = this.querySelector('button[name="prev"]');
      this.nextBtn = this.querySelector('button[name="next"]');
      this.currentMediaId = String(this.item?.dataset.mediaId || '');
      this.onClick = this.onClick.bind(this);
      this.onSwatchChange = this.onSwatchChange.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', () => this.prepare(), { once: true });
      this.addEventListener('touchstart', () => this.prepare(), { once: true, passive: true });
      this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.productCard?.addEventListener('change', this.onSwatchChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (ProductCardImageSlider.observer) ProductCardImageSlider.observer.observe(this);
      else this.prepare();
    }

    disconnectedCallback() {
      this.stopHoverAutoplay();
      ProductCardImageSlider.observer?.unobserve(this);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.productCard?.removeEventListener('change', this.onSwatchChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.initialized = false;
    }

    prepare() {
      if (this.media) return;
      try {
        this.media = JSON.parse(this.querySelector('[data-media-manifest]')?.textContent || '[]');
      } catch (error) {
        this.media = [];
      }
      this.currentIndex = Math.max(0, this.media.findIndex((entry) => String(entry.id) === this.currentMediaId));
      this.swatches = Array.from(this.productCard?.querySelectorAll('.card__swatches .opt-btn[data-media-id]') || []);
      const checkedMediaId = this.swatches.find((swatch) => swatch.checked)?.dataset.mediaId;
      if (checkedMediaId) this.selectMedia(checkedMediaId);
      this.toggleAttribute('inactive', this.getVisibleIndices().length < 2);
      this.setButtonStates();
    }

    getVisibleIndices() {
      if (!this.media?.length) return [];
      const anchors = this.swatches
        ?.map((swatch) => this.media.findIndex((entry) => String(entry.id) === String(swatch.dataset.mediaId)))
        .filter((index, position, values) => index >= 0 && values.indexOf(index) === position)
        .sort((a, b) => a - b) || [];
      if (!anchors.length) return this.media.map((entry, index) => index);

      const checkedId = this.swatches.find((swatch) => swatch.checked)?.dataset.mediaId;
      const checkedIndex = this.media.findIndex((entry) => String(entry.id) === String(checkedId || this.currentMediaId));
      let start = anchors.filter((index) => index <= checkedIndex).pop();
      if (start === undefined) start = anchors[0];
      const nextAnchor = anchors.find((index) => index > start);
      const end = nextAnchor === undefined ? this.media.length : nextAnchor;
      return this.media.map((entry, index) => index).slice(start, end);
    }

    selectMedia(mediaId) {
      this.prepare();
      const index = this.media.findIndex((entry) => String(entry.id) === String(mediaId));
      if (index >= 0) this.renderIndex(index);
    }

    renderIndex(index) {
      const entry = this.media?.[index];
      if (!entry || !this.image || !this.item || !this.link) return;
      this.currentIndex = index;
      this.currentMediaId = String(entry.id);
      this.item.dataset.mediaId = this.currentMediaId;
      this.image.dataset.mediaId = this.currentMediaId;
      this.image.sizes = entry.sizes || '100vw';
      this.image.srcset = entry.srcset || '';
      this.image.src = entry.src;
      this.image.alt = entry.alt || '';
      if (entry.width) this.image.width = entry.width;
      if (entry.height) this.image.height = entry.height;
      if (entry.url) this.link.href = entry.url;
      this.setButtonStates();
    }

    step(direction, wrap) {
      this.prepare();
      const visible = this.getVisibleIndices();
      if (visible.length < 2) return;
      let position = visible.indexOf(this.currentIndex);
      if (position < 0) position = 0;
      let next = position + direction;
      if (wrap) next = (next + visible.length) % visible.length;
      else next = Math.max(0, Math.min(visible.length - 1, next));
      this.renderIndex(visible[next]);
    }

    setButtonStates() {
      const visible = this.getVisibleIndices();
      const position = visible.indexOf(this.currentIndex);
      if (this.prevBtn) this.prevBtn.disabled = visible.length < 2 || position <= 0;
      if (this.nextBtn) this.nextBtn.disabled = visible.length < 2 || position === visible.length - 1;
    }

    onClick(event) {
      const button = event.target.closest('.slider-nav__btn');
      if (!button) return;
      event.preventDefault();
      this.stopHoverAutoplay();
      this.step(button.name === 'next' ? 1 : -1, false);
    }

    onSwatchChange(event) {
      if (!event.target.matches('.opt-btn[data-media-id]')) return;
      this.stopHoverAutoplay();
      this.swatches = Array.from(this.productCard.querySelectorAll('.card__swatches .opt-btn[data-media-id]'));
      this.selectMedia(event.target.dataset.mediaId);
      this.toggleAttribute('inactive', this.getVisibleIndices().length < 2);
      this.setButtonStates();
    }

    onPointerDown(event) {
      this.prepare();
      this.pointerStartX = event.clientX;
    }

    onPointerUp(event) {
      if (this.pointerStartX === undefined) return;
      const delta = event.clientX - this.pointerStartX;
      this.pointerStartX = undefined;
      if (Math.abs(delta) < 36) return;
      this.stopHoverAutoplay();
      this.step(delta < 0 ? 1 : -1, false);
    }

    canHoverAutoplay() {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onPointerEnter() {
      this.prepare();
      this.startHoverAutoplay();
    }

    onPointerLeave() {
      this.stopHoverAutoplay();
    }

    startHoverAutoplay() {
      if (document.hidden || !this.canHoverAutoplay() || this.getVisibleIndices().length < 2) return;
      if (ProductCardImageSlider.activeAutoplay && ProductCardImageSlider.activeAutoplay !== this) {
        ProductCardImageSlider.activeAutoplay.stopHoverAutoplay();
      }
      if (this.hoverAutoplayTimer) return;
      ProductCardImageSlider.activeAutoplay = this;
      this.hoverAutoplayTimer = window.setInterval(() => {
        if (document.hidden || !this.matches(':hover') || !this.isConnected) this.stopHoverAutoplay();
        else this.step(1, true);
      }, 1000);
    }

    stopHoverAutoplay() {
      if (this.hoverAutoplayTimer) window.clearInterval(this.hoverAutoplayTimer);
      this.hoverAutoplayTimer = null;
      if (ProductCardImageSlider.activeAutoplay === this) ProductCardImageSlider.activeAutoplay = null;
    }

    onVisibilityChange() {
      if (document.hidden) this.stopHoverAutoplay();
    }
  }

  customElements.define('product-card-image-slider', ProductCardImageSlider);
}
